import { createAction, handleActions } from 'redux-actions';
import {
  API_SUCCESSION_SUCCESSORS,
  api_succession_key_job,
  api_succession_key_job_change_log,
  api_succession_successor,
  api_succession_successor_remove,
} from 'constants/apiRoutes';
import http from 'utility/http';

// ------------------------------------
// Constants
// ------------------------------------

const SET_SUCCESSION_KEY_JOB = 'SET_SUCCESSION_KEY_JOB';
const SET_SUCCESSION_KEY_JOB_CHANGELOG = 'SET_SUCCESSION_KEY_JOB_CHANGELOG';
const SET_SUCCESSION_KEY_JOB_HOLDERS = 'SET_SUCCESSION_KEY_JOB_HOLDERS';
const SET_SUCCESSION_KEY_JOB_SUCCESSORS = 'SET_SUCCESSION_KEY_JOB_SUCCESSORS';
const LOAD_MORE_SUCCESSION_KEY_JOB_SUCCESSORS =
  'LOAD_MORE_SUCCESSION_KEY_JOB_SUCCESSORS';
const LOAD_MORE_SUCCESSION_KEY_JOB_CHANGELOG =
  'LOAD_MORE_SUCCESSION_KEY_JOB_CHANGELOG';

export const SUCCESSION_KEY_JOB_DEFAULT_PAGE = 1;
const PAGE_SIZE = 10;

// ------------------------------------
// Initial state
// ------------------------------------

const initialState = {
  keyJob: null,
  holders: [],
  successors: {},
  changelog: {},
};

// ------------------------------------
// Actions
// ------------------------------------

export const setSuccessionKeyJob = createAction(SET_SUCCESSION_KEY_JOB);
export const setSuccessionKeyJobChangelog = createAction(
  SET_SUCCESSION_KEY_JOB_CHANGELOG
);
export const setSuccessionKeyJobHolders = createAction(
  SET_SUCCESSION_KEY_JOB_HOLDERS
);
export const setSuccessionKeyJobSuccessors = createAction(
  SET_SUCCESSION_KEY_JOB_SUCCESSORS
);
const loadMoreSuccessionKeyJobSuccessors = createAction(
  LOAD_MORE_SUCCESSION_KEY_JOB_SUCCESSORS
);
const loadMoreSuccessionKeyJobChangelog = createAction(
  LOAD_MORE_SUCCESSION_KEY_JOB_CHANGELOG
);

// ------------------------------------
// Reducers
// ------------------------------------

const reducers = {
  [SET_SUCCESSION_KEY_JOB]: (state, { payload }) => ({
    ...state,
    keyJob: payload,
  }),
  [SET_SUCCESSION_KEY_JOB_CHANGELOG]: (state, { payload }) => ({
    ...state,
    changelog: payload || {},
  }),
  [LOAD_MORE_SUCCESSION_KEY_JOB_CHANGELOG]: (state, { payload }) => ({
    ...state,
    changelog: {
      ...payload,
      results: state.changelog.results
        ? [...state.changelog.results, ...(payload.results || [])]
        : [...(payload.results || [])],
    },
  }),
  [SET_SUCCESSION_KEY_JOB_HOLDERS]: (state, { payload }) => ({
    ...state,
    holders: payload || [],
  }),
  [SET_SUCCESSION_KEY_JOB_SUCCESSORS]: (state, { payload }) => ({
    ...state,
    successors: payload || {},
  }),
  [LOAD_MORE_SUCCESSION_KEY_JOB_SUCCESSORS]: (state, { payload }) => ({
    ...state,
    successors: {
      ...payload,
      results: state.successors.results
        ? [...state.successors.results, ...(payload.results || [])]
        : [...(payload.results || [])],
    },
  }),
};

// ------------------------------------
// API calls
// ------------------------------------

export const getSuccessionKeyJob = (dispatch, jobId) =>
  http.get(api_succession_key_job(jobId)).then(({ data }) => {
    dispatch(setSuccessionKeyJob(data));
    dispatch(setSuccessionKeyJobHolders(data?.holders || []));
    return data;
  });

export const getKeyJobSuccessors = (
  dispatch,
  jobId,
  showRemoved = false,
  isLoadMore = false,
  page = SUCCESSION_KEY_JOB_DEFAULT_PAGE
) =>
  http
    .get(API_SUCCESSION_SUCCESSORS, {
      params: {
        job: jobId,
        show_removed: showRemoved,
        page,
        page_size: PAGE_SIZE,
      },
    })
    .then(({ data }) => {
      dispatch(
        isLoadMore
          ? loadMoreSuccessionKeyJobSuccessors(data)
          : setSuccessionKeyJobSuccessors(data)
      );
      return data;
    });

export const getKeyJobChangelog = (
  dispatch,
  jobId,
  isLoadMore = false,
  page = SUCCESSION_KEY_JOB_DEFAULT_PAGE
) =>
  http
    .get(api_succession_key_job_change_log(jobId), {
      params: {
        page,
        page_size: PAGE_SIZE,
      },
    })
    .then(({ data }) => {
      dispatch(
        isLoadMore
          ? loadMoreSuccessionKeyJobChangelog(data)
          : setSuccessionKeyJobChangelog(data)
      );
      return data;
    });

export const postSuccessionKeyJobSuccessor = (jobId, payload = {}) => {
  const body = {
    job: jobId,
    employee: payload.user,
  };

  if (payload.readiness_level != null && payload.readiness_level !== '') {
    body.readiness = payload.readiness_level;
  }
  if (payload.note !== undefined) body.note = payload.note;
  if (payload.rank != null && Number(payload.rank) >= 1) {
    body.rank = Number(payload.rank);
  }
  if (payload.source_pool_id != null && payload.source_pool_id !== '') {
    body.source_pool_id = payload.source_pool_id;
  }

  return http.post(API_SUCCESSION_SUCCESSORS, body).then(({ data }) => data);
};

export const patchSuccessionKeyJobSuccessor = (
  jobId,
  successorId,
  payload = {}
) => {
  const body = {};

  if (payload.readiness_level != null && payload.readiness_level !== '') {
    body.readiness = payload.readiness_level;
  }
  if (payload.note !== undefined) body.note = payload.note;
  if (payload.rank != null && Number(payload.rank) >= 1) {
    body.rank = Number(payload.rank);
  }

  return http
    .patch(api_succession_successor(successorId), body)
    .then(({ data }) => data);
};

export const removeSuccessionKeyJobSuccessor = (
  jobId,
  successorId,
  payload = {}
) => {
  const body = { removal_reason: payload.reason };
  if (payload.comment !== undefined) body.removal_comment = payload.comment;

  return http
    .post(api_succession_successor_remove(successorId), body)
    .then(({ data }) => data);
};

export default handleActions(reducers, initialState);

// ------------------------------------
// "plain" action functions
// ------------------------------------

export const clearSuccessionKeyJob = dispatch => {
  dispatch(setSuccessionKeyJob(initialState.keyJob));
  dispatch(setSuccessionKeyJobChangelog(initialState.changelog));
  dispatch(setSuccessionKeyJobHolders(initialState.holders));
  dispatch(setSuccessionKeyJobSuccessors(initialState.successors));
};
